/**
 * Processes text before sending it to subtitle services.
 * Uses AI to apply dictionary replacements only when they make contextual sense.
 *
 * @param {string} text - Text to process
 * @param {string} sourceLanguage - Source language
 * @param {string} targetLanguage - Target language
 * @param {string} vendor - Service vendor
 * @returns {Promise<Object>} - Object with processed text and metadata
 */
export const handlePreRequestSubtitles = async (text, sourceLanguage, targetLanguage, vendor) => {
    const dictionary = window.dictionaries?.find(
        (dictionary) =>
            dictionary.sourceLanguage === sourceLanguage &&
            dictionary.targetLanguage === targetLanguage,
    );

    if (!dictionary || !dictionary.replacements || dictionary.replacements.length === 0) {
        return {
            text,
            sourceLanguage,
            targetLanguage,
            vendor,
        };
    }

    try {
        // OPTIMIZATION: Filter dictionary to only include words present in the text
        const relevantReplacements = dictionary.replacements.filter(r => {
            const pattern = new RegExp(`\\b${r.from}\\b`, 'gi');
            return pattern.test(text);
        });

        // If no relevant replacements found, return original text
        if (relevantReplacements.length === 0) {
            return {
                text,
                sourceLanguage,
                targetLanguage,
                vendor,
            };
        }

        // Build API path based on current location
        // Example: /box/Lira2/moderator/app -> /box/Lira2/moderator/api/analyze-context
        const basePath = window.location.pathname.split('/app')[0];
        const apiUrl = `${basePath}/api/analyze-context`;

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                text,
                replacements: relevantReplacements, // Send only relevant replacements
            }),
        });

        if (!response.ok) {
            console.warn('AI analysis failed, returning original text');
            return {
                text,
                sourceLanguage,
                targetLanguage,
                vendor,
            };
        }

        const data = await response.json();

        return {
            text: data.text || text,
            sourceLanguage,
            targetLanguage,
            vendor,
        };

    } catch (error) {
        console.error('Error calling AI analysis:', error);
        return {
            text,
            sourceLanguage,
            targetLanguage,
            vendor,
        };
    }
};
